import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAccountSource } from '@/src/renderer/lib/accountSource'
import { KeyIcon } from '@/src/renderer/components/icons'
import { Button } from '@/src/renderer/components/ui/Button'
import { PasswordInput } from '@/src/renderer/components/ui/PasswordInput'
import type { ResetPasswordFormProps } from './ResetPasswordForm.types'
import './ResetPasswordForm.scss'

export function ResetPasswordForm({ onBackToLogin }: ResetPasswordFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const account = useAccountSource()
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const newPasswordInputRef = useRef<HTMLInputElement>(null)

  // Auto-focus on new password input when component mounts
  useEffect(() => {
    newPasswordInputRef.current?.focus()
  }, [])

  // Keyboard shortcuts: ESC to go back, Enter to submit
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onBackToLogin) {
        e.preventDefault()
        onBackToLogin()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onBackToLogin])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Validation
    if (newPassword.length < 8) {
      setError(t('auth.resetPassword.passwordTooShort'))
      return
    }

    if (newPassword !== confirmPassword) {
      setError(t('auth.resetPassword.passwordMismatch'))
      return
    }

    setLoading(true)

    const result = await account.updatePassword(newPassword)

    if (result.success) {
      setSuccess(true)
      // Clear password recovery mode flags (both flag and hash)
      localStorage.removeItem('passwordRecoveryMode')
      localStorage.removeItem('passwordRecoveryHash')
      sessionStorage.removeItem('passwordRecoveryHash')
      // Show success message briefly, then redirect
      setTimeout(() => navigate('/home'), 2000)
    } else {
      setError(
        result.errorKey
          ? t(result.errorKey)
          : t('auth.resetPassword.errorGeneric')
      )
      setLoading(false)
    }
  }

  return (
    <div className="reset-password-form">
      {/* Header with Icon, Title, and Subtitle */}
      <div className="reset-password-form__header">
        <KeyIcon size={100} className="reset-password-form__icon" />
        <h2 className="reset-password-form__title">
          {t('auth.resetPassword.title')}
        </h2>
        <p className="reset-password-form__subtitle">
          {t('auth.resetPassword.subtitle')}
        </p>
      </div>

      {/* Form */}
      <form className="reset-password-form__form" onSubmit={handleSubmit}>
        <div className="reset-password-form__inputs">
          {/* New Password Input */}
          <PasswordInput
            ref={newPasswordInputRef}
            id="new-password"
            name="new-password"
            autoComplete="new-password"
            placeholder={t('auth.resetPassword.newPassword')}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={loading || success}
            error={!!error && newPassword.length > 0 && newPassword.length < 8}
          />

          {/* Confirm Password Input */}
          <PasswordInput
            id="new-password-confirm"
            name="new-password-confirm"
            autoComplete="new-password"
            placeholder={t('auth.resetPassword.confirmPassword')}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading || success}
            error={!!error && confirmPassword.length > 0 && newPassword !== confirmPassword}
          />
        </div>

        {/* Error Message */}
        {error && (
          <p className="reset-password-form__error">{error}</p>
        )}

        {/* Success Message */}
        {success && (
          <p className="reset-password-form__success">
            {t('auth.resetPassword.successMessage')}
          </p>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={loading || success}
          shortcut={{ kind: 'return' }}
        >
          {loading
            ? t('auth.resetPassword.submitting')
            : t('auth.resetPassword.submit')}
        </Button>
      </form>
    </div>
  )
}
